import type { Device } from '@studio/models/device';
import type { AutoTrackingState as AutoTrackingProtoState } from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { AutoTrackingReservation } from '@tryiris-ai/common.protojs/studio/v1/studio';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type {
  AutoTrackingReservationState,
  AutoTrackingReservationStatePair,
  AutoTrackingState
  // eslint-disable-next-line import/no-internal-modules
} from './provider/types.js';
import { stateIdWithReservation } from './utils.js';
// eslint-disable-next-line import/no-internal-modules
import type { Proxy } from '#~/agent/types.js';
import { toHexFromId } from '#~/api/index.js';

/**
 * Transforms a state reservation map into a js object.
 *
 * @param reservations - The reservation map that you want to transform.
 * @param proxy - The agent proxy.
 * @returns A transformed reservation map.
 */
export const transformAutoTrackingReservations = (
  reservations: Record<Device['id'], AutoTrackingReservation>,
  proxy?: Proxy
): Record<Device['id'], AutoTrackingReservationStatePair> => {
  const transformedReservations: Record<Device['id'], AutoTrackingReservationStatePair> = {};

  for (const [_, reservation] of Object.entries(reservations)) {
    if (!reservation) {
      continue;
    }

    const { agentId, cameraId, reservationId, workerId } = reservation;
    const deviceId = toHexFromId<Device['id']>(cameraId)!;

    if (!agentId || !cameraId) {
      continue;
    }

    //
    const stateId = stateIdWithReservation({
      agentId,
      reservationId,
      workerId
    });

    const workerIdHex = toHexFromId(workerId) ?? '';

    const autoTrackingState =
      proxy?.state?.control?.studio?.autoTrackingWorker?.[workerIdHex]?.state;

    const transformedReservation: AutoTrackingReservationState = {
      agentId,
      cameraId,
      id: deviceId,
      reservationId,
      stateId,
      stateStatus: autoTrackingState?.status?.status.toString() ?? '',
      workerId
    };

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const state = transformAutoTrackingState(autoTrackingState);

    const annotations =
      proxy?.getAutoTrackingAnnotations(toHexFromId<Device['id']>(cameraId)!) ?? [];

    transformedReservations[deviceId] = {
      annotations,
      reservation: transformedReservation,
      state
    };
  }

  return transformedReservations;
};

/**
 * Transforms a state into a js object.
 *
 * @param state - The state.
 * @returns The transformed state.
 */
export const transformAutoTrackingState = (
  state: T_Maybe<AutoTrackingProtoState>
): T_Maybe<AutoTrackingState> => {
  if (!state) {
    return undefined;
  }

  return {
    deadBand: state.control?.deadBand,

    detectionMode: state.control?.detectionMode,

    frameAlign: state.control?.frameAlign,
    frameZoom: state.control?.frameZoom,

    safePosition:
      state.control?.safePosition?.pan !== undefined &&
      state.control.safePosition.tilt !== undefined &&
      state.control.safePosition.zoom !== undefined
        ? {
            pan: state.control.safePosition.pan,
            tilt: state.control.safePosition.tilt,
            zoom: state.control.safePosition.zoom
          }
        : undefined,

    selectedSubjectId: state.control?.selectedSubjectId,

    shotSafetyFallback: state.control?.shotSafetyFallback,

    status: {
      detectionMode: state.status?.detectionMode
    },

    trackingSpeed: state.control?.trackingSpeed,

    zoomAdaptive: state.control?.zoomAdaptive,

    zoomManual: state.control?.zoomManual
  };
};
